import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { GlobalProvider } from './contexts/GlobalContext';
import { AuthProvider } from './contexts/AuthContext';
import { StationProvider } from './contexts/StationContext';
import { Layout } from './layouts/Layout';
import './App.css';
import { Helmet } from 'react-helmet';

const AudioLayout = lazy(() => import('./layouts/AudioLayout').then(module => ({ default: module.AudioLayout })));
const EventLayout = lazy(() => import('./layouts/EventLayout').then(module => ({ default: module.EventLayout })));
const RedlineLayout = lazy(() => import('./layouts/RedlineLayout').then(module => ({ default: module.RedlineLayout })));
const Home = lazy(() => import('./pages/Home'));
const AudioList = lazy(() => import('./pages/Audio/AudioList'));
const AudioDetails = lazy(() => import('./pages/Audio/AudioDetails'));
const Video = lazy(() => import('./pages/Video'));
const Rjs = lazy(() => import('./pages/Rjs'));
const Events = lazy(() => import('./pages/Events/EventList'));
const EventDetails = lazy(() => import('./pages/Events/EventDetails'));
const Redlines = lazy(() => import('./pages/Redlines/RedlineList'));
const RedlineDetails = lazy(() => import('./pages/Redlines/RedlineDetails'));
const TalentHunts = lazy(() => import('./pages/TalentHunt'));
const Careers = lazy(() => import('./pages/Footer/Careers'));
const ContactUs = lazy(() => import('./pages/Footer/ContactUs'));
const AdvertiseWithUs = lazy(() => import('./pages/Footer/AdvertiseWithUs'));
const PrivacyPolicy = lazy(() => import('./pages/Footer/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/Footer/TermsAndConditions'));
const CsrPolicy = lazy(() => import('./pages/Footer/CsrPolicy'));
const NotFound = lazy(() => import('./pages/404'));
function App() {
  const routes = createBrowserRouter([
    {
      path: "/",
      element: <Navigate to="/0/home" replace />,
    },
    {
      path: "/:stationUrlName",
      element: <Layout />,
      children: [
        { path: "/:stationUrlName/home", element: <Home /> },
        {
          path: "/:stationUrlName/audio", element: <AudioLayout />, children: [
            { path: "/:stationUrlName/audio", element: <AudioList /> },
            { path: "/:stationUrlName/audio/:audioUrlName", element: <AudioDetails /> },
          ]
        },
        { path: "/:stationUrlName/rjs/:rjUrlName", element: <Rjs /> },
        { path: "/:stationUrlName/video", element: <Video /> },
        {
          path: "/:stationUrlName/events", element: <EventLayout />, children: [
            { path: "/:stationUrlName/events", element: <Events /> },
            { path: "/:stationUrlName/events/:eventUrlName", element: <EventDetails /> },
          ]
        },
        {
          path: "/:stationUrlName/redlines", element: <RedlineLayout />, children: [
            { path: "/:stationUrlName/redlines", element: <Redlines /> },
            { path: "/:stationUrlName/redlines/:redlineUrlName", element: <RedlineDetails /> },
          ]
        },
        { path: "/:stationUrlName/talent-hunt", element: <TalentHunts /> },
        { path: "/:stationUrlName/careers", element: <Careers /> },
        { path: "/:stationUrlName/contact-us", element: <ContactUs /> },
        { path: "/:stationUrlName/advertise-with-us", element: <AdvertiseWithUs /> },
        { path: "/:stationUrlName/privacy-policy", element: <PrivacyPolicy /> },
        { path: "/:stationUrlName/terms-and-conditions", element: <TermsAndConditions /> },
        { path: "/:stationUrlName/csr-policy", element: <CsrPolicy /> },
        { path: "*", element: <NotFound /> }
      ],
    },
    { path: "/careers", element: <Navigate to="/0/careers" replace /> },
    { path: "/contact-us", element: <Navigate to="/0/contact-us" replace /> },
    { path: "/advertise-with-us", element: <Navigate to="/0/advertise-with-us" replace /> },
    { path: "/privacy-policy", element: <Navigate to="/0/privacy-policy" replace /> },
    { path: "/terms-and-conditions", element: <Navigate to="/0/terms-and-conditions" replace /> },
    { path: "/csr-policy", element: <Navigate to="/0/csr-policy" replace /> },
    { path: "*", element: <Navigate to="/0/404" replace /> }
  ]);

  return (
    <>
      <Helmet>
        <meta name="google-adsense-account" content={import.meta.env.VITE_ADSENSE_CLIENT_ID} />
        <meta name="google-site-verification" content={import.meta.env.VITE_GOOGLE_SITE_VERIFICATION} />
        <script async src={`https://www.googletagmanager.com/gtag/js?id=${import.meta.env.VITE_GOOGLE_ANALYTICS_ID}`}></script>
        <script>
          {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${import.meta.env.VITE_GOOGLE_ANALYTICS_ID}');
        `}

        </script>
      </Helmet>
      <GlobalProvider>
        <AuthProvider>
          <StationProvider>
            <Suspense fallback={<div className="flex justify-center items-center h-screen bg-black"></div>}>
              <RouterProvider router={routes} />
            </Suspense>
          </StationProvider>
        </AuthProvider>
      </GlobalProvider>
    </>
  )
}

export default App